import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAiConversation,
  getAiConversations,
  getAiSuggestedQuestions,
  sendAiChatMessage,
  sendAiFeedback,
} from "../../services/aiAssistantService";

const MAX_SUGGESTIONS = 6;

const unwrap = (data) => data?.Result ?? data?.result ?? data;

const getErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  return (
    data?.Message ||
    data?.message ||
    data?.Error ||
    data?.error ||
    err?.message ||
    fallback
  );
};

const normalizeLanguage = (value) => {
  const code = String(value || "").toLowerCase().slice(0, 2);
  return code === "de" ? "de" : "en";
};

const getInitialLanguage = () => {
  const stored =
    localStorage.getItem("languageCode") ||
    localStorage.getItem("uiLanguage") ||
    localStorage.getItem("language");
  return normalizeLanguage(stored || "en");
};

const getContextFromPath = (pathname) => {
  const path = pathname.toLowerCase();
  const parts = path.split("/").filter(Boolean);

  if (path === "/dashboard" || path === "/") {
    return { screenName: "Dashboard", eventId: null };
  }

  if (path === "/events") {
    return { screenName: "Events", eventId: null };
  }

  if (parts[0] === "events" && parts[1] && parts[2] === "gallery") {
    return { screenName: "Albums", eventId: Number(parts[1]) || parts[1] };
  }

  if (parts[0] === "events" && parts[1]) {
    return { screenName: "EventDetails", eventId: Number(parts[1]) || parts[1] };
  }

  if (parts[0] === "gallery" || parts[0] === "upload" || parts[0] === "uploads") {
    return { screenName: "Galleries", eventId: null };
  }

  if (parts[0] === "rooms" && parts[2] === "gallery") {
    return { screenName: "Albums", eventId: null };
  }

  if (parts[0] === "rooms") {
    return { screenName: "Assistants", eventId: null };
  }

  if (parts[0] === "auto-upload") {
    return { screenName: "Galleries", eventId: null };
  }

  if (parts[0] === "companion") {
    return { screenName: "Assistants", eventId: null };
  }

  if (parts[0] === "wallet") {
    return { screenName: "Wallet", eventId: null };
  }

  if (parts[0] === "settings") {
    return { screenName: "Settings", eventId: null };
  }

  if (parts[0] === "qr") {
    return { screenName: "QR", eventId: null };
  }

  return { screenName: "PromoterPanel", eventId: null };
};

const normalizeSuggestion = (item) => {
  if (typeof item === "string") return item;
  return item?.Question || item?.question || item?.Text || item?.text || item?.Title || item?.title || "";
};

const getConversationTitle = (item) =>
  item?.Title ||
  item?.title ||
  item?.LastMessage ||
  item?.lastMessage ||
  item?.Message ||
  item?.message ||
  `Conversation ${item?.Id || item?.id || ""}`.trim();

const getConversationId = (item) =>
  item?.Id ?? item?.id ?? item?.ConversationId ?? item?.conversationId;

const normalizeConversationMessages = (data) => {
  const result = unwrap(data);
  const rawMessages =
    result?.Messages ||
    result?.messages ||
    result?.ChatMessages ||
    result?.chatMessages ||
    result?.Items ||
    result?.items ||
    [];

  if (!Array.isArray(rawMessages)) return [];

  return rawMessages
    .map((msg, index) => {
      const role = String(msg?.Role || msg?.role || msg?.Sender || msg?.sender || "").toLowerCase();
      const text =
        msg?.Message ||
        msg?.message ||
        msg?.Content ||
        msg?.content ||
        msg?.Text ||
        msg?.text ||
        "";

      if (!text) return null;

      const isAssistant =
        role.includes("assistant") ||
        role.includes("ai") ||
        role.includes("bot") ||
        msg?.IsAssistant === true ||
        msg?.isAssistant === true;

      return {
        id: msg?.Id || msg?.id || `history-${index}`,
        role: isAssistant ? "assistant" : "user",
        text,
        assistantMessageId: isAssistant ? msg?.Id || msg?.id : null,
      };
    })
    .filter(Boolean);
};

const PSAAIAssistant = () => {
  const location = useLocation();
  const messageEndRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [languageCode, setLanguageCode] = useState(getInitialLanguage);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState(null);

  const pageContext = useMemo(
    () => getContextFromPath(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!open) return;

    setLoadingSuggestions(true);
    getAiSuggestedQuestions(languageCode)
      .then((res) => {
        const data = unwrap(res.data);
        const list = Array.isArray(data)
          ? data
          : data?.Questions || data?.questions || data?.Items || data?.items || [];
        setSuggestions(
          list
            .map(normalizeSuggestion)
            .filter(Boolean)
            .slice(0, MAX_SUGGESTIONS)
        );
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, [open, languageCode]);

  useEffect(() => {
    if (!open) return;
    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending, open]);

  const refreshConversations = async () => {
    setLoadingConversations(true);
    try {
      const res = await getAiConversations();
      const data = unwrap(res.data);
      const list = Array.isArray(data)
        ? data
        : data?.Items || data?.items || data?.Conversations || data?.conversations || [];
      setConversations(list);
    } catch {
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && conversations.length === 0) {
      refreshConversations();
    }
  };

  const loadConversation = async (item) => {
    const id = getConversationId(item);
    if (!id) return;

    setLoadingConversationId(id);
    setError("");
    try {
      const res = await getAiConversation(id);
      const loadedMessages = normalizeConversationMessages(res.data);
      setConversationId(id);
      setMessages(loadedMessages);
      setHistoryOpen(false);
    } catch (err) {
      setError(getErrorMessage(err, "Could not open that conversation."));
    } finally {
      setLoadingConversationId(null);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setFeedbackByMessageId({});
    setError("");
  };

  const submitMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setSending(true);
    setError("");

    try {
      const res = await sendAiChatMessage({
        message: trimmed,
        conversationId,
        screenName: pageContext.screenName,
        eventId: pageContext.eventId,
        languageCode,
      });
      const data = unwrap(res.data);

      if (data?.success === false || data?.Success === false) {
        throw new Error(data?.error || data?.Error || data?.message || data?.Message || "AI Assistant could not answer.");
      }

      const nextConversationId =
        data?.conversationId ?? data?.ConversationId ?? conversationId;
      const assistantMessageId =
        data?.assistantMessageId ?? data?.AssistantMessageId ?? null;
      const answer =
        data?.message ||
        data?.Message ||
        data?.answer ||
        data?.Answer ||
        "I could not find an answer for that.";

      if (nextConversationId) setConversationId(nextConversationId);

      setMessages((prev) => [
        ...prev,
        {
          id: assistantMessageId || `assistant-${Date.now()}`,
          role: "assistant",
          text: answer,
          assistantMessageId,
        },
      ]);
    } catch (err) {
      const message = getErrorMessage(err, "AI Assistant is unavailable right now.");
      setError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "assistant",
          text: message,
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const submitFeedback = async (assistantMessageId, rating) => {
    if (!assistantMessageId || feedbackByMessageId[assistantMessageId]) return;

    try {
      await sendAiFeedback({
        messageId: assistantMessageId,
        rating,
        comment: null,
      });
      setFeedbackByMessageId((prev) => ({
        ...prev,
        [assistantMessageId]: rating,
      }));
      toast.success("Feedback sent");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to send feedback."));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitMessage(draft);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          style={styles.launcher}
          onClick={() => setOpen(true)}
          aria-label="Open PSA AI Assistant"
        >
          <span style={styles.launcherIcon}>AI</span>
          <span>PSA AI Assistant</span>
        </button>
      )}

      {open && (
        <div style={styles.overlay} onClick={() => setOpen(false)}>
          <section
            style={styles.panel}
            onClick={(event) => event.stopPropagation()}
            aria-label="PSA AI Assistant"
          >
            <header style={styles.header}>
              <div>
                <h2 style={styles.title}>PSA AI Assistant</h2>
                <p style={styles.context}>
                  {pageContext.screenName}
                  {pageContext.eventId ? ` / Event ${pageContext.eventId}` : ""}
                </p>
              </div>
              <div style={styles.headerActions}>
                <select
                  value={languageCode}
                  onChange={(event) => setLanguageCode(normalizeLanguage(event.target.value))}
                  style={styles.languageSelect}
                  aria-label="Assistant language"
                >
                  <option value="en">EN</option>
                  <option value="de">DE</option>
                </select>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={toggleHistory}
                  title="Conversation history"
                >
                  H
                </button>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={startNewConversation}
                  title="New conversation"
                >
                  +
                </button>
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={() => setOpen(false)}
                  title="Close"
                >
                  x
                </button>
              </div>
            </header>

            {historyOpen && (
              <div style={styles.history}>
                <div style={styles.historyHeader}>
                  <span style={styles.historyTitle}>Recent conversations</span>
                  <button type="button" style={styles.linkButton} onClick={refreshConversations}>
                    Refresh
                  </button>
                </div>
                {loadingConversations ? (
                  <p style={styles.muted}>Loading conversations...</p>
                ) : conversations.length === 0 ? (
                  <p style={styles.muted}>No previous conversations yet.</p>
                ) : (
                  <div style={styles.historyList}>
                    {conversations.slice(0, 8).map((item) => {
                      const id = getConversationId(item);
                      return (
                        <button
                          key={id || getConversationTitle(item)}
                          type="button"
                          style={styles.historyItem}
                          onClick={() => loadConversation(item)}
                          disabled={loadingConversationId === id}
                        >
                          {loadingConversationId === id ? "Opening..." : getConversationTitle(item)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {error && <div style={styles.errorBanner}>{error}</div>}

            <div style={styles.messages}>
              {messages.length === 0 && !sending ? (
                <div style={styles.emptyState}>
                  <h3 style={styles.emptyTitle}>How can I help?</h3>
                  <p style={styles.emptyText}>
                    Ask about PSA features, QR flows, galleries, events, uploads, or daily promoter support.
                  </p>
                  <div style={styles.suggestions}>
                    {loadingSuggestions ? (
                      <span style={styles.muted}>Loading suggestions...</span>
                    ) : suggestions.length > 0 ? (
                      suggestions.map((question) => (
                        <button
                          key={question}
                          type="button"
                          style={styles.suggestionChip}
                          onClick={() => submitMessage(question)}
                        >
                          {question}
                        </button>
                      ))
                    ) : (
                      <>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage("What is eQR?")}>
                          What is eQR?
                        </button>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage("What is pQR?")}>
                          What is pQR?
                        </button>
                        <button type="button" style={styles.suggestionChip} onClick={() => submitMessage("Explain 1-Click-Family-Event")}>
                          Explain 1-Click-Family-Event
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    style={{
                      ...styles.messageRow,
                      ...(message.role === "user" ? styles.userRow : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.bubble,
                        ...(message.role === "user" ? styles.userBubble : styles.assistantBubble),
                        ...(message.isError ? styles.errorBubble : {}),
                      }}
                    >
                      {message.text}
                    </div>
                    {message.role === "assistant" && message.assistantMessageId && (
                      <div style={styles.feedback}>
                        <button
                          type="button"
                          style={{
                            ...styles.feedbackButton,
                            ...(feedbackByMessageId[message.assistantMessageId] === 1 ? styles.feedbackActive : {}),
                          }}
                          onClick={() => submitFeedback(message.assistantMessageId, 1)}
                          title="Helpful"
                        >
                          &#128077;
                        </button>
                        <button
                          type="button"
                          style={{
                            ...styles.feedbackButton,
                            ...(feedbackByMessageId[message.assistantMessageId] === -1 ? styles.feedbackActive : {}),
                          }}
                          onClick={() => submitFeedback(message.assistantMessageId, -1)}
                          title="Not helpful"
                        >
                          &#128078;
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
              {sending && (
                <div style={styles.messageRow}>
                  <div style={{ ...styles.bubble, ...styles.assistantBubble, ...styles.loadingBubble }}>
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={messageEndRef} />
            </div>

            <form style={styles.inputBar} onSubmit={handleSubmit}>
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Ask PSA AI Assistant..."
                style={styles.input}
                rows={1}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    submitMessage(draft);
                  }
                }}
              />
              <button
                type="submit"
                style={styles.sendButton}
                disabled={sending || !draft.trim()}
                title="Send"
              >
                Send
              </button>
            </form>
          </section>
        </div>
      )}
    </>
  );
};

const styles = {
  launcher: {
    position: "fixed",
    right: 24,
    bottom: 24,
    zIndex: 1040,
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 16px",
    border: "1px solid rgba(108,99,255,0.3)",
    borderRadius: 999,
    background: "#6c63ff",
    color: "#fff",
    boxShadow: "0 8px 24px rgba(31,41,55,0.22)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
  },
  launcherIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1150,
    background: "rgba(17,24,39,0.34)",
    display: "flex",
    justifyContent: "flex-end",
  },
  panel: {
    width: "min(440px, 100vw)",
    height: "100vh",
    background: "#fff",
    boxShadow: "-10px 0 30px rgba(17,24,39,0.22)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid #eef0f4",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    margin: 0,
    fontSize: 17,
    lineHeight: 1.25,
    color: "#1e1e2d",
  },
  context: {
    margin: "4px 0 0",
    color: "#888",
    fontSize: 12,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  languageSelect: {
    height: 32,
    border: "1px solid #d8dbe3",
    borderRadius: 8,
    color: "#1e1e2d",
    background: "#fff",
    fontSize: 12,
    fontWeight: 700,
    padding: "0 6px",
  },
  iconButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dbe3",
    borderRadius: 8,
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
  },
  history: {
    borderBottom: "1px solid #eef0f4",
    padding: "12px 16px",
    background: "#fafbff",
  },
  historyHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  historyTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#1e1e2d",
  },
  linkButton: {
    border: "none",
    background: "transparent",
    color: "#6c63ff",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  historyList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  historyItem: {
    width: "100%",
    textAlign: "left",
    border: "1px solid #eceef5",
    background: "#fff",
    borderRadius: 8,
    padding: "8px 10px",
    color: "#374151",
    cursor: "pointer",
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  errorBanner: {
    margin: "12px 16px 0",
    padding: "9px 11px",
    borderRadius: 8,
    background: "#fff5f5",
    border: "1px solid #fecaca",
    color: "#b91c1c",
    fontSize: 12,
    lineHeight: 1.45,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    background: "#f8f9fb",
  },
  emptyState: {
    background: "#fff",
    border: "1px solid #eef0f4",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  emptyTitle: {
    margin: "0 0 6px",
    fontSize: 16,
    color: "#1e1e2d",
  },
  emptyText: {
    margin: "0 0 14px",
    color: "#666",
    fontSize: 13,
    lineHeight: 1.5,
  },
  suggestions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  suggestionChip: {
    border: "1px solid #d8d4ff",
    background: "#f4f2ff",
    color: "#5b54d6",
    borderRadius: 999,
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  muted: {
    margin: 0,
    color: "#888",
    fontSize: 12,
  },
  messageRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 5,
  },
  userRow: {
    alignItems: "flex-end",
  },
  bubble: {
    maxWidth: "84%",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userBubble: {
    background: "#6c63ff",
    color: "#fff",
    borderBottomRightRadius: 4,
  },
  assistantBubble: {
    background: "#fff",
    color: "#333",
    border: "1px solid #eef0f4",
    borderBottomLeftRadius: 4,
  },
  errorBubble: {
    background: "#fff5f5",
    borderColor: "#fecaca",
    color: "#b91c1c",
  },
  loadingBubble: {
    color: "#888",
    fontStyle: "italic",
  },
  feedback: {
    display: "flex",
    gap: 6,
    marginLeft: 4,
  },
  feedbackButton: {
    border: "1px solid #d8dbe3",
    background: "#fff",
    color: "#6b7280",
    borderRadius: 999,
    padding: "3px 8px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  feedbackActive: {
    borderColor: "#6c63ff",
    color: "#6c63ff",
    background: "#f4f2ff",
  },
  inputBar: {
    padding: 14,
    borderTop: "1px solid #eef0f4",
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    background: "#fff",
  },
  input: {
    flex: 1,
    minHeight: 42,
    maxHeight: 110,
    resize: "vertical",
    border: "1px solid #d8dbe3",
    borderRadius: 10,
    padding: "11px 12px",
    fontFamily: "inherit",
    fontSize: 13,
    lineHeight: 1.35,
    outline: "none",
  },
  sendButton: {
    height: 42,
    border: "none",
    borderRadius: 10,
    background: "#6c63ff",
    color: "#fff",
    padding: "0 16px",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default PSAAIAssistant;
